import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, ChevronDown, X } from 'lucide-react';
import Button from './Button';
import './HomePage.css';

interface WinnerData {
  id: string;
  name: string;
  image: string;
  type: string;
  snippet: string;
  fullBio: string;
}

const HomePage: React.FC = () => {
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const scrollToMission = () => {
    const missionElement = document.getElementById('mission');
    if (missionElement) {
      missionElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const winners2026: WinnerData[] = [
    {
      id: "adelyn-tate",
      name: "Adelyn Tate",
      image: "/images/adelyn-tate.png",
      type: "Athletic Scholarship",
      snippet: "Adelyn is a 2026 graduate of Xenia High School. During her time there she was a 4-year varsity letter athlete for volleyball, and also earned a 2-year varsity letter for bowling.",
      fullBio: "Adelyn is a 2026 graduate of Xenia High School. During her time there she was a 4-year varsity letter athlete for volleyball. Along with volleyball, she also earned 2-year varsity letter for bowling. Her freshman and sophomore year she received the Buc Spirit Award. Her positive energy never went unnoticed kept the spirits of the team alive. Adelyn was a captain of her team her junior and senior year. During her senior season, she helped lead her team to a District Runner Up Trophy. Aside from volleyball, Adelyn was involved in Student Council, National Honor Society, Chemistry Club, Spanish Club, HOSA, and Blue Crew. She also held leadership positions including President of the Student Council, Vice President of National Honor Society, and student leader of the Blue Crew. In the fall, Adelyn will be attending the University of Dayton to major in health sciences with an end goal of becoming a Pediatric Physician Assistant. I would like to thank everyone who can make the Carly Faye Foundation possible. Thank you, and Go Flyers!"
    },
    {
      id: "annabel-seall",
      name: "Annabel Seall",
      image: "/images/annabel-seall.png",
      type: "Academic Scholarship",
      snippet: "Annabel is a 2026 graduate of Catholic Central High School. She was an active member of National Honors Society, student council, and student ambassador.",
      fullBio: "Annabel is a 2026 graduate of Catholic Central High School. Annabel was an active member of National Honors Society, student council, and student ambassador. Annabel also participated in theater and was a member of the Lady Irish varsity Softball team. After high school Annabel plans on attending DePaul University to major in filmmaking."
    }
  ];

  const winners2025: WinnerData[] = [
    {
      id: "elise-dunaway",
      name: "Elise Dunaway",
      image: "/images/elise-dunaway.jpeg",
      type: "Athletic Scholarship",
      snippet: "I'm so honored and grateful to be a recipient of the Carly Faye Foundation Scholarship. This opportunity means the world to me—not just because of the financial support, but because it reminds me that hard work, kindness, and staying true to yourself really matter.",
      fullBio: "I'm so honored and grateful to be a recipient of the Carly Faye Foundation Scholarship. This opportunity means the world to me—not just because of the financial support, but because it reminds me that hard work, kindness, and staying true to yourself really matter. I'll be starting my journey into dental hygiene soon, a field I chose because I've always loved helping people feel more confident, especially through their smiles. This scholarship has given me even more motivation to pursue my goals and make a positive impact in people's lives. I'm truly thankful to be part of something so meaningful, and I hope to carry Carly's legacy with me throughout my career and beyond."
    },
    {
      id: "claire-dewitt",
      name: "Claire Dewitt",
      image: "/images/claire-dewitt.jpeg",
      type: "Academic Scholarship",
      snippet: "I am a graduate of the class of 2025 at Catholic Central High School, where I participated in soccer and tennis. I was also a student ambassador and the treasurer of the student body government.",
      fullBio: "Hello, my name is Claire Dewitt. I am a graduate of the class of 2025 at Catholic Central High School, where I participated in soccer and tennis. I was also a student ambassador and the treasurer of the student body government. I will be continuing my studies at Ohio State University, majoring in biology on the pre-medical track. I would like to thank my family, friends, teachers, and coaches for always being there for me, as well as everyone who has made the Carly Faye Foundation possible. Thank you, and Go Bucks!"
    }
  ];

  const winners2024: WinnerData[] = [
    {
      id: "lauren-byrd",
      name: "Lauren Byrd",
      image: "/images/lauren-byrd.jpg",
      type: "Athletic Scholarship",
      snippet: "Lauren is a 2024 graduate of Northwestern High School. During her time at Northwestern, Lauren was a 2-year varsity letter winner for both volleyball and basketball.",
      fullBio: "Lauren is a 2024 graduate of Northwestern High School. During her time at Northwestern, Lauren was a 2-year varsity letter winner for both volleyball and basketball. During her senior volleyball season, Lauren was a captain who helped lead her team to districts. Lauren was awarded 2nd Team CBC, All District 9 2nd Team, All Clark County Volleyball, and District Senior All-Star Volleyball. Lauren also holds a school record dating back to 2007 for the most kills in a season. Lauren was also a captain of the Northwestern girl's basketball team. She broke two school records throughout her senior season, including blocks per game and total blocks in a season. She also was awarded 1st Team CBC, All-District 9 Team, and Senior All-Star Basketball. Aside from athletics, Lauren was involved in the National Honor Society, SADD, Sources of Strength, Student Council, and Junior Achievement. She held many leadership positions including National Honor Society Secretary and Senior Class Secretary. Lauren was also a 4 year Jan Van Gorder honor scholar student. In the fall, Lauren plans to attend Miami University to major in marketing at the Farmer School of Business."
    },
    {
      id: "avery-guenther",
      name: "Avery Guenther",
      image: "/images/avery-guenther.jpg",
      type: "Academic Scholarship",
      snippet: "Avery is a graduate of Catholic Central High School, Springfield Ohio. She was a 4 year member of both the soccer and swim teams.",
      fullBio: "Avery is a graduate of Catholic Central High School, Springfield Ohio. She was a 4 year member of both the soccer and swim teams. Throughout high school Avery was a member of National Honors Society, Student Ambassadors, Student Council, Building Buddies, and was a retreat leader. After high school she plans on attending Ohio University, and majoring in nursing."
    },
    {
      id: "jordyn-smoot",
      name: "Jordyn Smoot",
      image: "/images/jordyn-smoot.jpg",
      type: "Academic Scholarship",
      snippet: "Jordyn Smoot is a 2024 graduate from Catholic Central High School. She participated in softball for two years and track for one year.",
      fullBio: "Jordyn Smoot is a 2024 graduate from Catholic Central High School. She participated in softball for two years and track for one year. She also participated in basketball for four years where she was named team captain for the past two years. In this past year Jordyn was named OHC South Player of the Year, 1st Team OHC, Clark County Miss Basketball, 1st team Clark County, 1st team district 9, 1st team Southwest district, and special mention All Ohio. Jordyn was a member of the National Honors Society, Student ambassador, and a retreat leader. Jordyn plans to attend Ohio Christian university where she will continue her basketball career and major in psychology."
    }
  ];

  const openModal = (id: string) => {
    setActiveModal(id);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setActiveModal(null);
    document.body.style.overflow = 'auto';
  };

  // Auto-scroll slideshow functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 7); // 7 slides total
    }, 3000); // Change slide every 3 seconds

    return () => clearInterval(interval);
  }, []);

  // Update slide display
  useEffect(() => {
    const slides = document.querySelectorAll('.glo-slide');
    
    slides.forEach((slide, index) => {
      slide.classList.toggle('active', index === currentSlide);
    });
  }, [currentSlide]);

  return (
    <div className="homepage">
      <Helmet>
        <title>Carly Faye Foundation – Empowering Young Women</title>
        <meta name="description" content="Partnering with girls as they battle for excellence through academic and athletic scholarships. Discover our mission and scholarship recipients." />
        <link rel="canonical" href="https://carly-faye-foundation.web.app/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Carly Faye Foundation – Empowering Young Women" />
        <meta property="og:description" content="Partnering with girls as they battle for excellence through academic and athletic scholarships." />
        <meta property="og:url" content="https://carly-faye-foundation.web.app/" />
        <meta property="og:image" content="/images/cf-logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Carly Faye Foundation – Empowering Young Women" />
        <meta name="twitter:description" content="Partnering with girls as they battle for excellence through academic and athletic scholarships." />
        <meta name="twitter:image" content="/images/cf-logo.png" />
      </Helmet>
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background">
          <img src="/images/carly-faye-foundation-hero.jpg" alt="" className="hero-image" />
          <div className="hero-overlay"></div>
        </div>
        
        <div className="hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-text"
          >
            <h1 className="hero-title">
            Empowering Young Women to Overcome, Achieve, and Thrive.
            </h1>
            <div className="hero-buttons">
              <Button href="/about" variant="primary" size="large">
                Our Story
              </Button>
              <Button href="/donate" variant="outline" size="large">
                Make a Difference
              </Button>
            </div>
          </motion.div>
        </div>
        <button className="scroll-indicator" onClick={scrollToMission}>
          <span>Discover</span>
          <ChevronDown size={20} />
        </button>
      </section>

      {/* Mission Statement & Impact Section */}
      <section id="mission" className="mission-statement">
        <div className="container">
          <div className="mission-impact-layout">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="mission-content"
            >
              <span className="overline">Our Mission</span>
              <h2>Turning Loss Into Light</h2>
              <p className="mission-intro">
                The Carly Faye Foundation honors the legacy of Carly Faye DeLong by providing scholarships 
                and support to young women who demonstrate the same determination, passion, and excellence 
                that defined Carly's life.
              </p>
              <p>
                Through our programs, we aim to inspire the next generation of female leaders to overcome 
                obstacles and pursue their dreams with unwavering commitment.
              </p>
              <p>
                Each year, we award scholarships to exceptional young women who embody Carly's spirit 
                through their dedication to academic excellence and athletic achievement.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="program-card academic-card"
            >
              <div className="program-image">
                <img src="/images/carly-faye-academic.png" alt="Academic Scholarship" />
                <div className="program-overlay"></div>
              </div>
              <div className="program-content">
                <h3>Academic Scholarship</h3>
                <p>
                  Providing scholarships to graduating seniors who demonstrate 
                  the hard work and dedication to academia that was characteristic of Carly DeLong.
                </p>
                <Link to="/scholarships" className="text-link">
                  Learn More <ArrowUpRight size={16} />
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="program-card athletic-card"
            >
              <div className="program-image">
                <img src="/images/carly-faye-athletic.jpg" alt="Athletic Scholarship" />
                <div className="program-overlay"></div>
              </div>
              <div className="program-content">
                <h3>Athletic Scholarship</h3>
                <p>
                  Supporting high school volleyball players who demonstrate passion, love for the game, 
                  teamwork, and dedication both on and off the court.
                </p>
                <Link to="/scholarships" className="text-link">
                  Learn More <ArrowUpRight size={16} />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Winners Section - New standalone section with distinct style */}
      <section className="winners-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <span className="overline">Recognizing Excellence</span>
            <h2>2026 Scholarship Recipients</h2>
            <p>Meet the inspiring young women who are carrying forward Carly's legacy</p>
          </motion.div>
          
          <div className="recipients-grid current-year">
            {winners2026.map((winner, index) => (
              <motion.div
                key={winner.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="recipient-card"
                data-id={winner.id}
                onClick={() => openModal(winner.id)}
              >
                <div className="recipient-image">
                  <img src={winner.image} alt={winner.name} />
                </div>
                <div className="recipient-content">
                  <div className="recipient-badge">{winner.type}</div>
                  <h3>{winner.name}</h3>
                  <p>{winner.snippet}</p>
                  <button className="recipient-more">
                    View {winner.name.split(' ')[0]}'s Bio <ArrowRight size={16} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Previous Recipients Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="section-header previous-recipients"
          >
            <h3>Previous Scholarship Recipients</h3>
          </motion.div>
          
          <div className="recipients-grid">
            {[...winners2025, ...winners2024].map((winner, index) => (
              <motion.div
                key={winner.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 + (index * 0.1) }}
                viewport={{ once: true }}
                className="recipient-card"
                data-id={winner.id}
                onClick={() => openModal(winner.id)}
              >
                <div className="recipient-image">
                  <img src={winner.image} alt={winner.name} />
                </div>
                <div className="recipient-content">
                  <div className="recipient-badge">{winner.type}</div>
                  <h3>{winner.name}</h3>
                  <p>{winner.snippet}</p>
                  <button className="recipient-more">
                    View {winner.name.split(' ')[0]}'s Bio <ArrowRight size={16} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GLO Section */}
      <section className="glo-section">
        <div className="container">
          <div className="glo-layout">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="glo-slideshow"
            >
              <div className="glo-slideshow-container">
                <div className="glo-slides">
                  <div className="glo-slide active">
                    <img src="/images/glo/gallery/DSC_0595.jpg" alt="GLO girl getting ready" />
                  </div>
                  <div className="glo-slide">
                    <img src="/images/glo/gallery/IMG_9875.jpg" alt="GLO transformation moment" />
                  </div>
                  <div className="glo-slide">
                    <img src="/images/glo/gallery/AZ6A9863-Enhanced-NR.jpg" alt="GLO transformation" />
                  </div>
                  <div className="glo-slide">
                    <img src="/images/glo/gallery/DSC_0596.jpg" alt="GLO moment" />
                  </div>
                  <div className="glo-slide">
                    <img src="/images/glo/gallery/IMG_9861.jpg" alt="GLO experience" />
                  </div>
                  <div className="glo-slide">
                    <img src="/images/glo/gallery/AZ6A9871-Enhanced-NR.jpg" alt="GLO transformation" />
                  </div>
                  <div className="glo-slide">
                    <img src="/images/glo/gallery/DSC_0583.jpg" alt="GLO moment" />
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="glo-content"
            >
              <h2>Girls Lifting Others</h2>
              <p>
                GLO is a program of The Carly Faye Foundation that provides transformative makeover days for girls ages 10-21 facing pediatric cancer. Through professional hair styling, makeup, and photography, we help these brave young women see themselves as confident, radiant, and ready to face the future.
              </p>
              <p>
                Every GLO day is filled with laughter, support, and the magic of confidence. Local high school girls volunteer to support GLO Girls during their makeovers, creating a community of strength and sisterhood.
              </p>
              <Button href="/glo" variant="primary" icon={ArrowRight}>
                Learn About GLO
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Carly's Story Section */}
      <section className="story-section">
        <div className="container">
          <div className="story-layout">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="story-content"
            >
              <span className="overline">Her Legacy</span>
              <h2>Carly Faye DeLong</h2>
              <p>
              On New Year's Eve 2016, Carly was diagnosed with Acute Lymphoblastic Leukemia (ALL). From the very beginning, she refused to let her diagnosis define her. She fought with everything she had to overcome this obstacle and went on to live a full and impactful life.
              </p>
              <p>
              The Carly Faye Foundation honors her courage by inspiring others to keep climbing.
              </p>
              <div className="story-quote">
                <blockquote>
                  "In the echo of her laughter, mountains still move."
                </blockquote>
              </div>
              <Button href="/about" variant="secondary" icon={ArrowRight}>
                Read Her Full Story
              </Button>
            </motion.div>
            <div className="story-image-container">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="story-image"
              >
                <img src="/images/carly/IMG_6585.jpg" alt="Carly Faye DeLong" />
                <div className="story-image-accent"></div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-background">
          <div className="cta-overlay"></div>
        </div>
        
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="cta-content"
          >
            <h2>
              Join Our Mission
            </h2>
            <p>Help us support young women in their pursuit of academic and athletic excellence</p>
            <div className="cta-buttons">
              <Button href="/donate" variant="primary" size="large" icon={ArrowRight}>
                Make a Donation
              </Button>
              <Button href="/contact" variant="outline" size="large">
                Get Involved
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Winner Modals */}
      {[...winners2026, ...winners2025, ...winners2024].map((winner) => (
        <div 
          key={winner.id} 
          className={`modal ${activeModal === winner.id ? 'modal-active' : ''}`}
          onClick={closeModal}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <X size={24} />
            </button>
            
            <div className="modal-header">
              <div className="modal-image">
                <img src={winner.image} alt={winner.name} />
              </div>
              <div className="modal-title">
                <h3>{winner.name}</h3>
                <span className="modal-type">{winner.type}</span>
              </div>
            </div>
            <div className="modal-body">
              <p>{winner.fullBio}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default HomePage; 